import { useQuery, keepPreviousData } from '@tanstack/react-query'
import { useApiClient } from '@/shared/api'

export interface CallCountItem {
  calls: number
  interval_start: string
}

export interface ConnectivityItem {
  connectivity_rate: number
  interval_start: string
}

export interface RealtimeData {
  stepSeconds: number
  days: number
  totalCalls: CallCountItem[]
  successfulCalls: CallCountItem[]
  connectedCalls: ConnectivityItem[]
  callsOver30s: CallCountItem[]
  summaryTotalCalls?: CallCountItem[]
  summarySuccessfulCalls?: CallCountItem[]
  summaryConnectedCalls?: ConnectivityItem[]
  summaryCallsOver30s?: CallCountItem[]
}

interface RealtimeMetricsOptions {
  stepSeconds?: number
  days?: number
  campaignId?: number | null
  refetchIntervalMs?: number
}

export function useRealtimeMetrics({
  stepSeconds = 1800,
  days = 2,
  campaignId = null,
  refetchIntervalMs = 30_000,
}: RealtimeMetricsOptions = {}) {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: ['realtime', stepSeconds, days, campaignId],
    queryFn: async () => {
      const params = new URLSearchParams({
        stepSeconds: stepSeconds.toString(),
        days: days.toString(),
      })
      if (campaignId) params.set('campaign_id', campaignId.toString())
      const response = await apiClient<{ success: boolean; data: RealtimeData }>(
        `/realtime?${params.toString()}`
      )
      return response.data
    },
    staleTime: Math.min(refetchIntervalMs, 30_000),
    refetchInterval: refetchIntervalMs,
    placeholderData: keepPreviousData,
  })
}

export function useRealtimeChartData(stepSeconds: number, days: number, enabled: boolean = true) {
  const apiClient = useApiClient()

  return useQuery({
    queryKey: ['realtime-chart', stepSeconds, days],
    queryFn: async () => {
      const params = new URLSearchParams({
        stepSeconds: stepSeconds.toString(),
        days: days.toString(),
      })
      const response = await apiClient<{ success: boolean; data: RealtimeData }>(
        `/realtime?${params.toString()}`
      )
      return response.data
    },
    enabled,
    staleTime: 30 * 1000,
  })
}